import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  getLibraryCategoryToneClassName,
  libraryCategoryPalette,
  type LibraryCategory,
  type LibraryCategoryColorKey,
} from "@/lib/libraryCategories";
import { LibraryCategoryBadge } from "./LibraryCategoryBadge";

type LibraryCategoryPickerProps = {
  categories: LibraryCategory[];
  value: string[];
  onChange: (value: string[]) => void;
  onCreateCategory: (values: { name: string; color: LibraryCategoryColorKey }) => Promise<LibraryCategory>;
  disabled?: boolean;
};

const normalize = (value: string) => value.trim().toLowerCase();

export const LibraryCategoryPicker = ({
  categories,
  value,
  onChange,
  onCreateCategory,
  disabled = false,
}: LibraryCategoryPickerProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [createColor, setCreateColor] = useState<LibraryCategoryColorKey>(libraryCategoryPalette[0].value);

  const selectedCategories = useMemo(
    () => categories.filter((category) => value.includes(category.id)),
    [categories, value],
  );

  const filteredCategories = useMemo(() => {
    const q = normalize(query);
    if (!q) return categories;
    return categories.filter((category) => normalize(category.name).includes(q));
  }, [categories, query]);

  const canCreate = Boolean(query.trim()) && !categories.some((category) => normalize(category.name) === normalize(query));

  const toggleCategory = (categoryId: string) => {
    if (value.includes(categoryId)) {
      onChange(value.filter((id) => id !== categoryId));
      return;
    }

    onChange([...value, categoryId]);
  };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name) return;

    setCreating(true);
    try {
      const created = await onCreateCategory({ name, color: createColor });
      onChange([...value, created.id]);
      setQuery("");
      setOpen(false);
      toast.success("Category created");
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-3">
      {selectedCategories.length ? (
        <div className="flex flex-wrap gap-2">
          {selectedCategories.map((category) => (
            <LibraryCategoryBadge
              key={category.id}
              category={category}
              onRemove={() => toggleCategory(category.id)}
            />
          ))}
        </div>
      ) : null}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className="w-full justify-between font-normal text-left"
          >
            <span className="truncate text-foreground">
              {selectedCategories.length ? `${selectedCategories.length} categor${selectedCategories.length === 1 ? "y" : "ies"} selected` : "Assign categories…"}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(26rem,var(--radix-popover-trigger-width))] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search or create a category…"
              onKeyDown={(event) => {
                if (event.key === "Enter" && canCreate) {
                  event.preventDefault();
                  void handleCreate();
                }
              }}
            />
            <CommandList>
              {!filteredCategories.length && !canCreate ? <CommandEmpty>No categories yet.</CommandEmpty> : null}
              {filteredCategories.length ? (
                <CommandGroup heading="Categories">
                  {filteredCategories.map((category) => {
                    const selected = value.includes(category.id);
                    return (
                      <CommandItem key={category.id} value={category.name} onSelect={() => toggleCategory(category.id)}>
                        <Check className={cn("mr-2 h-4 w-4", selected ? "opacity-100" : "opacity-0")} />
                        <span className={cn("mr-2 h-2.5 w-2.5 rounded-full border", getLibraryCategoryToneClassName(category.color))} />
                        <span className="truncate">{category.name}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>

          {canCreate ? (
            <div className="border-t border-border p-3 space-y-3">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Color</Label>
                <div className="flex flex-wrap gap-2">
                  {libraryCategoryPalette.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      aria-label={option.label}
                      aria-pressed={createColor === option.value}
                      className={cn(
                        "h-8 w-8 rounded-full border transition hover:scale-105",
                        option.toneClassName,
                        createColor === option.value && "ring-2 ring-ring ring-offset-2 ring-offset-popover",
                      )}
                      onClick={() => setCreateColor(option.value)}
                    />
                  ))}
                </div>
              </div>
              <Button type="button" size="sm" className="w-full" onClick={() => void handleCreate()} disabled={creating}>
                <Plus className="h-4 w-4" /> {creating ? "Creating…" : `Create “${query.trim()}”`}
              </Button>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
};
